import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/ui/StatCard";
import { Card, CardHeader } from "../components/ui/Card";
import { Spinner, EmptyState } from "../components/ui/Spinner";
import { Logo } from "../components/ui/Logo";
import Calendar from "./calendar/Calendar";
import Notes from "./notes/Notes";
import DashboardInbox from "./messages/DashboardInbox";
import { ItalianFlag } from "../components/ui/ItalianFlag";
import { GermanFlag } from "../components/ui/GermanFlag";
import { formatDateTime } from "../utils/formatDate";
import type { DashboardStats } from "../types";

const REFRESH_INTERVAL_MS = 30_000;

/**
 * Dashboard-Startseite. Neben der Mitgliederzahl und den
 * Benutzeraktivitäten sind Kalender und Notizen direkt hier eingebettet
 * (siehe pages/calendar/Calendar.tsx, pages/notes/Notes.tsx - dieselben
 * Komponenten wie zuvor als eigenständige Seiten, jetzt ohne eigenen
 * Menüpunkt/Route). Jeder Bereich respektiert weiterhin seine eigene
 * Berechtigung ("calendar.view"/"notes.use"/"activity.view") und wird nur
 * angezeigt, wenn der Benutzer sie besitzt - unverändert gegenüber vorher,
 * nur eben direkt auf dieser Seite statt über einen eigenen Reiter erreicht.
 *
 * Die Kennzahl wird periodisch im Hintergrund neu geladen (ohne erneutes
 * Spinner-Aufblitzen), damit z.B. die Mitgliederzahl auch dann aktuell
 * bleibt, wenn ein Mitglied in einem anderen Tab/von einem anderen
 * Benutzer angelegt oder gelöscht wurde.
 */
export default function Dashboard() {
  const { hasPermission, user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    return api.get<DashboardStats>("/api/reports/dashboard").then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    const interval = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  if (loading) return <Spinner />;
  if (!stats) return <EmptyState message="Keine Daten verfügbar." />;

  const showCalendar = hasPermission("calendar.view");
  const showNotes = hasPermission("notes.use");
  const showActivity = hasPermission("activity.view");
  // Persönliche Wortmarken-Variante NUR für dieses eine Benutzerkonto (per
  // Anforderung) - bewusst über den Benutzernamen statt einer neuen
  // Berechtigung/Einstellung, da es sich um eine rein individuelle,
  // nicht wiederverwendbare Anpassung für genau diesen einen Login handelt.
  const isSkullDevils = user?.username === "fratellis";

  return (
    <div className="relative space-y-8">
      {/* Sehr dezentes Wasserzeichen - rein dekorativ, keine Interaktion, stört die Lesbarkeit nicht */}
      <Logo className="hidden lg:block h-80 w-auto absolute -right-8 -top-4 opacity-[0.04] pointer-events-none select-none -z-10" />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-100 break-words">Willkommen im LBV-System von Phantom of Hell 👋</h1>
          {/* Für "skulldevilsmc" bewusst ausgeblendet (siehe Anforderung). */}
          {!isSkullDevils && <p className="text-sm text-gray-500 mt-1">Hier ist Ihr aktueller Überblick.</p>}
        </div>

        {/* Füllt die freie Mitte der Kopfzeile auf breiten Bildschirmen -
            auf schmalen/mittleren Breiten (flex-col) entsteht diese Lücke
            gar nicht erst, daher erst ab "lg" sichtbar. */}
        <div className="hidden lg:flex flex-col items-center flex-1 justify-center px-6 min-w-0 gap-1.5">
          {/* "Phantom of Hell x La Fratellanza Chipilo" ist deutlich länger als
              "Phantom of Hell" - bei gleicher Schriftgröße würde der Text
              hinter der Mitglieder-Kachel rechts verschwinden, daher hier
              eine kleinere Größenstufe speziell für diese Variante. */}
          <div className="flex items-center gap-4">
            {isSkullDevils && <GermanFlag className="h-7 w-11 shrink-0" />}
            <span className={`phantom-wordmark ${isSkullDevils ? "text-base xl:text-xl !whitespace-normal text-center [text-wrap:balance]" : "text-3xl xl:text-4xl"}`}>
              {isSkullDevils ? (
                <>Phantom of Hell x <span className="skull-devils-pulse">La Fratellanza Chipilo</span></>
              ) : (
                "Phantom of Hell"
              )}
            </span>
            {isSkullDevils && <ItalianFlag className="h-7 w-11 shrink-0" />}
          </div>
          {!isSkullDevils && (
            <span className="flex items-center gap-3 text-gray-400 text-xl font-display tracking-[0.3em]">
              <span aria-hidden="true">🩶</span>
              <span>P.F.F.P</span>
              <span aria-hidden="true">🩶</span>
            </span>
          )}
        </div>

        {/* Mitgliederzahl-Kachel: für "fratellis" bewusst ausgeblendet
            (siehe Anforderung) - rein persönliche Anpassung für diesen
            einen Login, siehe isSkullDevils oben. */}
        {!isSkullDevils && (
          <StatCard
            label="Mitglieder"
            value={String(stats.active_members_count)}
            icon={<span>🪪</span>}
            tone="default"
          />
        )}
      </div>

      {isSkullDevils && <DashboardInbox />}

      {showCalendar && (
        <section>
          <h2 className="text-xs font-display font-semibold uppercase tracking-widest text-sand-muted mb-3">📅 Kalender</h2>
          <Calendar />
        </section>
      )}

      {showNotes && <Notes />}

      {showActivity && (
        <Card>
          <CardHeader
            title="Benutzeraktivitäten"
            subtitle="Letzte Systemänderungen"
            action={<Link to="/activity" className="text-xs text-accent hover:underline">Alle anzeigen</Link>}
          />
          {stats.recentActivity.length === 0 ? (
            <EmptyState message="Keine Aktivitäten." />
          ) : (
            <ul className="space-y-3">
              {stats.recentActivity.map((entry) => (
                <li key={entry.id} className="text-sm">
                  <p className="text-gray-300">{entry.description}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(entry.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
